import { Link, createFileRoute, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { ArkivedTerminal } from '@/components/engineering/ProductVisuals'
import { LedgerSection, LedgerStamp } from '@/components/ledger/LedgerBlocks'
import { localizedPageHead } from '@/lib/seo-head'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'

export const Route = createFileRoute('/{-$locale}/products')({
  head: ({ params }) => localizedPageHead('products', params.locale),
  component: ProductsPage,
})

const CLOUD_AREAS = ['compute', 'databases', 'storage', 'metering'] as const

function ProductsPage() {
  const { t } = useTranslation('lab')
  const locale = getLocaleFromPath(useLocation().pathname)
  const href = (path: string) => withLocalePath(path, locale)
  return (
    <div>
      <header className="ledger-wrap pb-10 pt-12 md:pt-16">
        <h1 className="ledger-display max-w-[20ch] text-[clamp(2.4rem,6vw,4.4rem)]">
          {t('productShowcase.heroTitle')}
        </h1>
        <p className="ledger-lead mt-6">{t('productShowcase.heroIntro')}</p>
        <p className="ledger-muted mt-3 text-[0.9375rem]">
          {t('productShowcase.heroFoot')}
        </p>
      </header>

      <LedgerSection id="product-cloud" title="Horizon Cloud">
        <div className="grid gap-8 py-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <LedgerStamp>{t('status.inProgress')}</LedgerStamp>
            <h3 className="ledger-serif mt-5 text-[1.75rem] leading-tight">
              {t('productShowcase.cloud.title')}
            </h3>
            <p className="ledger-prose mt-4">
              {t('productShowcase.cloud.intro')}
            </p>
            <Link
              to={href('/cloud')}
              className="ledger-link ledger-focus mt-6"
            >
              {t('productPages.explore', { name: 'Horizon Cloud' })}
              <ArrowUpRight size={16} aria-hidden="true" />
            </Link>
          </div>
          <ul className="m-0 list-none self-start border-t border-[var(--rule-strong)] p-0 lg:col-span-5">
            {CLOUD_AREAS.map((key) => (
              <li
                key={key}
                className="border-b border-[var(--rule)] py-3 text-[1rem]"
              >
                {t(`productShowcase.cloud.${key}`)}
              </li>
            ))}
            <li className="ledger-muted py-3 text-[0.9375rem]">
              {t('productShowcase.cloud.growing')}
            </li>
          </ul>
        </div>
      </LedgerSection>

      <LedgerSection id="product-arkived" title="arkived">
        <div className="grid gap-8 py-8 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <LedgerStamp tone="ink">
              {`${t('status.inProgress')} · ${t('status.openSource')}`}
            </LedgerStamp>
            <h3 className="ledger-serif mt-5 text-[1.75rem] leading-tight">
              {t('productShowcase.arkived.title')}
            </h3>
            <p className="ledger-prose mt-4">
              {t('productShowcase.arkived.intro')}
            </p>
            <p className="ledger-muted mt-4 text-[0.9375rem]">
              <strong className="font-semibold text-[var(--ink)]">
                {t('productShowcase.arkived.detailTitle')}
              </strong>
              {' · '}
              {t('productShowcase.arkived.detail')}
            </p>
            <Link
              to={href('/products/arkived')}
              className="ledger-link ledger-focus mt-6"
            >
              {t('productPages.explore', { name: 'arkived' })}
              <ArrowUpRight size={16} aria-hidden="true" />
            </Link>
          </div>
          <div className="lg:col-span-6">
            <ArkivedTerminal />
          </div>
        </div>
      </LedgerSection>

      <section className="ledger-wrap py-12">
        <h2 className="ledger-serif text-[1.75rem] leading-tight">
          {t('productShowcase.followTitle')}
        </h2>
        <p className="ledger-prose mt-3">{t('productShowcase.followBody')}</p>
        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
          <Link to={href('/blog')} className="ledger-link ledger-focus">
            {t('journal.writing')}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
          <Link to={href('/about')} className="ledger-link ledger-focus">
            {t('journal.companyLink')}
            <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  )
}
